import type { SVGProps } from 'react';

/**
 * Jednotná sada liniových ikon (24×24, stroke = currentColor).
 * Bez externí knihovny — cesty jsou kreslené ručně v jednom stylu,
 * aby ikony v hlavičce i na úvodní stránce vypadaly konzistentně
 * (emoji se na každém systému vykreslují jinak).
 */

const paths = {
  building: (
    <>
      <path d="M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16" />
      <path d="M15 10h4a1 1 0 0 1 1 1v10" />
      <path d="M3 21h18" />
      <path d="M8 8h3M8 12h3M8 16h3M17.5 14h0M17.5 17.5h0" />
    </>
  ),
  newspaper: (
    <>
      <path d="M5 4h12a1 1 0 0 1 1 1v14a2 2 0 0 0 2 2H6a2 2 0 0 1-2-2V5a1 1 0 0 1 1-1Z" />
      <path d="M18 9h2a1 1 0 0 1 1 1v9a2 2 0 0 1-2 2" />
      <path d="M8 8h6v4H8zM8 15.5h6" />
    </>
  ),
  link: (
    <>
      <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1.2 1.2" />
      <path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1.2-1.2" />
    </>
  ),
  message: (
    <>
      <path d="M20 15a2 2 0 0 1-2 2H9l-5 4V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2Z" />
      <path d="M8.5 9h7M8.5 12.5h4.5" />
    </>
  ),
  /** Hlavičkový papír s logem vlevo nahoře. */
  letterhead: (
    <>
      <path d="M6 3h9l4 4v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <rect x="8" y="6.5" width="4" height="4" rx="1" fill="currentColor" stroke="none" />
      <path d="M8 14h8M8 17.5h5" />
    </>
  ),
  phone: (
    <path d="M5 4h3.5l1.7 4.2-2.2 1.4a11 11 0 0 0 6.4 6.4l1.4-2.2L20 15.5V19a1 1 0 0 1-1 1A16 16 0 0 1 4 5a1 1 0 0 1 1-1Z" />
  ),
  medical: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
      <path d="M12 8v8M8 12h8" />
    </>
  ),
  usersCheck: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="m15.5 11 2 2 3.5-4" />
    </>
  ),
  bandage: (
    <>
      <path d="M4.5 13.5 13.5 4.5a3.5 3.5 0 0 1 5 0l1 1a3.5 3.5 0 0 1 0 5l-9 9a3.5 3.5 0 0 1-5 0l-1-1a3.5 3.5 0 0 1 0-5Z" />
      <path d="M10 10h0M14 14h0M12 12h0M10 14h0M14 10h0" />
    </>
  ),
  warning: (
    <>
      <path d="M10.3 4.2 2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9.5v4M12 17h0" />
    </>
  ),
  /** Plamen — požární dohled. */
  flame: (
    <>
      <path d="M12 21a6.5 6.5 0 0 0 6.5-6.5c0-3.5-2.5-5.5-3.5-8.5-1.5 2-2.5 3-3.5 3 0-2.5-1-4.5-2.5-6 .3 3.5-4.5 6.5-4.5 11.5A6.5 6.5 0 0 0 12 21Z" />
      <path d="M12 21a2.8 2.8 0 0 1-2.8-2.8c0-1.8 1.6-2.8 2.8-4.7 1.2 1.9 2.8 2.9 2.8 4.7A2.8 2.8 0 0 1 12 21Z" />
    </>
  ),
  /** Průkaz zaměstnance. */
  idCard: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="11" r="2" />
      <path d="M5.5 16a3 3 0 0 1 6 0M14 10h4M14 13.5h3" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="m11 12 8.5-8.5M16.5 6.5l2.5 2.5M14 9l2 2" />
    </>
  ),
  handshake: (
    <>
      <path d="m11 17 2 2a1.4 1.4 0 0 0 2-2" />
      <path d="m14 14 2.5 2.5a1.4 1.4 0 0 0 2-2l-3.9-3.9a2 2 0 0 0-2.8 0l-.9.9a1.4 1.4 0 0 1-2-2l2.8-2.8a3.8 3.8 0 0 1 4.5-.6l.5.3a3 3 0 0 0 2 .3L21 6" />
      <path d="m21 3 1 9h-2M3 3 2 12l6.5 6.5a1.4 1.4 0 0 0 2-2M3 4h8" />
    </>
  ),
  list: (
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <path d="M4.5 6h0M4.5 12h0M4.5 18h0" />
    </>
  ),
  clipboardCheck: (
    <>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4V3h6v1a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1Z" />
      <path d="m9 13 2 2 4-4.5" />
    </>
  ),
  receipt: (
    <>
      <path d="M5 3h14v18l-2.3-1.5L14.3 21 12 19.5 9.7 21l-2.4-1.5L5 21Z" />
      <path d="M9 8h6M9 11.5h6M9 15h3.5" />
    </>
  ),
  tag: (
    <>
      <path d="M3 12V4a1 1 0 0 1 1-1h8l9 9-9 9Z" />
      <circle cx="7.5" cy="7.5" r="1.5" />
    </>
  ),
  cart: (
    <>
      <path d="M3 4h2.5l2.2 11h11l2-8H6.3" />
      <circle cx="9" cy="19.5" r="1.3" />
      <circle cx="17" cy="19.5" r="1.3" />
    </>
  ),
  alertOctagon: (
    <>
      <path d="M8.3 3h7.4L21 8.3v7.4L15.7 21H8.3L3 15.7V8.3Z" />
      <path d="M12 8v4.5M12 16h0" />
    </>
  ),
  signature: (
    <>
      <path d="M3 17c3-1 4.5-9 6.5-9s-1 8 1 8 2.5-4 4-4 .5 3 2 3 2-1 2.5-1.5" />
      <path d="M3 21h18" />
    </>
  ),
  book: (
    <>
      <path d="M5 4.5A1.5 1.5 0 0 1 6.5 3H19v15H6.5A1.5 1.5 0 0 0 5 19.5Z" />
      <path d="M5 19.5A1.5 1.5 0 0 0 6.5 21H19v-3" />
      <path d="M9 7h6M9 10.5h4" />
    </>
  ),
  calendarCheck: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
      <path d="m9 15 2 2 4-4" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8a1 1 0 0 1 1-1h2.5L9 4.5h6L16.5 7H19a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-4.4-4.4" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.6 9.5a2.5 2.5 0 0 1 4.8.8c0 1.7-2.4 2.2-2.4 3.7M12 17h0" />
    </>
  ),
  gantt: (
    <>
      <path d="M3 4v16h18" />
      <path d="M7 7h6M10 11h8M8 15h5" />
    </>
  ),
  fileDiff: (
    <>
      <path d="M6 3h9l4 4v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M12 8.5v5M9.5 11h5M9.5 17h5" />
    </>
  ),
  contacts: (
    <>
      <rect x="4" y="3" width="15" height="18" rx="2" />
      <circle cx="11.5" cy="10" r="2.5" />
      <path d="M7.5 17a4 4 0 0 1 8 0M19 7h2M19 12h2M19 17h2" />
    </>
  ),
  folderCheck: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <path d="m9 14 2 2 4-4" />
    </>
  ),
  wallet: (
    <>
      <path d="M4 7a2 2 0 0 1 2-2h11v4" />
      <path d="M4 7v11a2 2 0 0 0 2 2h14V9H6a2 2 0 0 1-2-2Z" />
      <path d="M16.5 14.5h0" />
    </>
  ),
  car: (
    <>
      <path d="M5 16v-4l2-5a1 1 0 0 1 1-.7h8a1 1 0 0 1 1 .7l2 5v4" />
      <path d="M3.5 16h17v2.5a1 1 0 0 1-1 1H18a1 1 0 0 1-1-1V18H7v.5a1 1 0 0 1-1 1H4.5a1 1 0 0 1-1-1Z" />
      <path d="M5 12h14M7.5 14.5h0M16.5 14.5h0" />
    </>
  ),
  blueprint: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1.5" />
      <path d="M3 9h7v11M10 13h11M14 4v5" />
    </>
  ),
  archive: (
    <>
      <rect x="3" y="4" width="18" height="4.5" rx="1" />
      <path d="M5 8.5V19a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8.5" />
      <path d="M10 12.5h4" />
    </>
  ),
  checkSquare: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
      <path d="m8 12 3 3 5-6" />
    </>
  ),
  crane: (
    <>
      <path d="M6 21V4l14 3H6M6 4 3 7h3" />
      <path d="M17 7v5M15.5 12h3v2h-3zM3 21h8M6 10l3-3M6 15l3-3" />
    </>
  ),
  share: (
    <>
      <circle cx="18" cy="5.5" r="2.5" />
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="18.5" r="2.5" />
      <path d="m8.2 10.8 7.6-4M8.2 13.2l7.6 4" />
    </>
  ),
  printer: (
    <>
      <path d="M7 8V3h10v5" />
      <rect x="3" y="8" width="18" height="9" rx="2" />
      <path d="M7 14h10v7H7z" />
    </>
  ),
  smartphone: (
    <>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path d="M11 18.5h2" />
    </>
  ),
  layers: (
    <>
      <path d="m12 3 9 5-9 5-9-5Z" />
      <path d="m3 12.5 9 5 9-5M3 17l9 5 9-5" />
    </>
  ),
  bell: (
    <>
      <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15Z" />
      <path d="M10 20.5a2 2 0 0 0 4 0" />
    </>
  ),
  save: (
    <>
      <path d="M5 3h11l4 4v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M8 3v5h7V3M8 21v-6h8v6" />
    </>
  ),
  contrast: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3a9 9 0 0 1 0 18Z" fill="currentColor" />
    </>
  ),
} as const;

export type IconName = keyof typeof paths;

type IconProps = Omit<SVGProps<SVGSVGElement>, 'name'> & {
  name: IconName;
  size?: number;
};

export function Icon({ name, size = 18, strokeWidth = 1.8, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
      {...rest}
    >
      {paths[name]}
    </svg>
  );
}
